import { OverflowDirection } from "@redotech/react-util/overflow";
import { OmsCodeStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import CodeIcon from "@redotech/redo-icons/code-01_filled.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { OverflowTooltip } from "@redotech/redo-web/tooltip/overflow-tooltip";
import { Handle, Position } from "@xyflow/react";
import { useState } from "react";

import { useTranslation } from "@redotech/redo-i18n";
import { PartialWithRequiredType } from "../../steps";
import { BaseNode } from "../base-node";
import * as NodeCSS from "../node.module.css";

export interface OmsCodeNodeData {
  step: PartialWithRequiredType<OmsCodeStep>;
  deleteElements: (id: string) => void;
}

export function OmsCodeNode(data: {
  id: string;
  data: OmsCodeNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const { step } = data.data;
  const [overflowRef, setOverflowRef] = useState<HTMLElement | null>(null);
  let detail: React.ReactNode;
  if (step.name) {
    detail = (
      <OverflowTooltip
        direction={OverflowDirection.Vertical}
        overflowRef={overflowRef}
        tooltipProps={{ title: step.name }}
      >
        <Text
          fontSize="xs"
          ref={setOverflowRef}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {step.name}
        </Text>
      </OverflowTooltip>
    );
  } else if (step.artifactId) {
    detail = (
      <Text fontFamily="monospace" fontSize="xs">
        {shorten(step.artifactId)}
      </Text>
    );
  } else {
    detail = <Text textColor="disabled">{t("No artifact uploaded")}</Text>;
  }
  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        icon={<RedoFeaturedIcon color="success" Icon={CodeIcon} size="xs" />}
        onDelete={() => data.data.deleteElements(data.id)}
        selected={data.selected}
        step={step}
        title={t("Code")}
      >
        <Flex
          className={NodeCSS.fullInput}
          fontSize="xs"
          gap="none"
          wrap="wrap"
        >
          {detail}
        </Flex>
      </BaseNode>
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}

function shorten(id: string): string {
  if (id.length <= 10) {
    return id;
  }
  return `${id.slice(0, 6)}…${id.slice(-3)}`;
}
